import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Inventory2 as PackageIcon,
  LocationOn as LocationIcon,
  LocalOffer as PriceIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import api from '../config/api';
import PublicPackageDetails from './PublicPackageDetails';
import DocumentPageOverlay, { documentOverlayActionSx } from '../components/DocumentPageOverlay';
import PartnerWorkspaceCard, {
  PartnerCardDatePanel,
  partnerCardTitleWideSx,
} from '../components/PartnerWorkspaceCard';
import PartnerPackagesSubnav from '../components/PartnerPackagesSubnav';
import {
  PartnerEmptyState,
  PartnerFilterBar,
  PartnerLoading,
  PartnerNote,
  PartnerPageHeader,
  PartnerPageShell,
  partnerLinkSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import { introductionFeeCopyVars } from '../config/plans';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import usePartnerMobileLayout from '../hooks/usePartnerMobileLayout';
import { isPackageHighlighted, isPackageInPublicCatalog, planNameKey } from '../utils/partnerPlan';
import BookForClientDialog from '../components/BookForClientDialog';
import RelistPackageDialog from '../components/RelistPackageDialog';
import {
  formatMoney,
  getDestinationLabel,
  getDestinationPhotoUrl,
  getPackageBucket,
  getPackageCoverSrc,
  getPackageListFilter,
  packageMatchesListFilter,
  matchesQuery,
} from '../utils/partnerListUtils';

const PartnerPackages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const isMobile = usePartnerMobileLayout();
  const customizeMode = searchParams.get('customize') === '1';
  const { entitlements, introductionFeeRate } = usePartnerEntitlements();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState('booked');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [notice, setNotice] = useState('');
  const [bookForClientPkg, setBookForClientPkg] = useState(null);
  const [relistPkg, setRelistPkg] = useState(null);
  const [relistError, setRelistError] = useState('');
  const [catalogLink, setCatalogLink] = useState(null);
  const [catalogCopied, setCatalogCopied] = useState(false);
  const [catalogRotating, setCatalogRotating] = useState(false);
  const [widgetCopied, setWidgetCopied] = useState(false);
  const [sellerComplete, setSellerComplete] = useState(true);

  useEffect(() => {
    fetchPackages();
    loadCatalogLink();
    loadSellerStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSellerStatus = async () => {
    try {
      const response = await api.get('/partners/profile', { headers: getAuthHeaders() });
      setSellerComplete(Boolean(response.data?.partner?.seller_reference_number));
    } catch (err) {
      // Non-blocking: the API still enforces the gate on create/publish.
      console.error('Error loading seller status:', err);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  };

  const loadCatalogLink = async () => {
    try {
      const response = await api.get('/partners/packages-catalog', { headers: getAuthHeaders() });
      setCatalogLink(response.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Error loading catalog link:', err);
      }
    }
  };

  const catalogToken = catalogLink?.token || catalogLink?.catalogToken || catalogLink?.packages_catalog_token;

  const handleCopyWidgetSnippet = async () => {
    try {
      let token = catalogToken;
      let link = catalogLink;
      if (!token) {
        const response = await api.get('/partners/packages-catalog', { headers: getAuthHeaders() });
        setCatalogLink(response.data);
        link = response.data;
        token = response.data?.token || response.data?.catalogToken;
      }
      if (!token) return;
      const widgetHost = (process.env.REACT_APP_DEVELOPER_PUBLIC_URL || 'https://developer.dejavunomad.com').replace(/\/$/, '');
      const snippet = `<script src="${widgetHost}/widget.js"></script>\n<div data-dvn-catalog="${token}"></div>`;
      await navigator.clipboard.writeText(snippet);
      setWidgetCopied(true);
      setNotice(t('partnerCatalog.widgetCopied'));
      setTimeout(() => setWidgetCopied(false), 2000);
      void link;
    } catch {
      setError(t('partnerCatalog.widgetCopyFailed'));
    }
  };

  const handleCopyCatalogLink = async () => {
    try {
      let url = catalogLink?.url;
      if (!url) {
        const response = await api.get('/partners/packages-catalog', { headers: getAuthHeaders() });
        setCatalogLink(response.data);
        url = response.data?.url;
      }
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setCatalogCopied(true);
      setNotice(t('partnerCatalog.copySuccess'));
      setTimeout(() => setCatalogCopied(false), 2000);
    } catch {
      setError(t('partnerCatalog.copyFailed'));
    }
  };

  const handleRotateCatalogLink = async () => {
    setCatalogRotating(true);
    setError('');
    try {
      const response = await api.post('/partners/packages-catalog/rotate', {}, { headers: getAuthHeaders() });
      setCatalogLink(response.data);
      setNotice(t('partnerCatalog.rotateSuccess'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCatalog.rotateFailed'));
    } finally {
      setCatalogRotating(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/partner-packages/my-packages?limit=200', {
        headers: getAuthHeaders()
      });
      setPackages(response.data.packages || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
      if (err.response?.status === 401) {
        navigate('/partner/login');
      } else {
        setError(t('partnerLists.loadPackagesFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRelist = async (dates) => {
    if (!relistPkg?.id) return;
    try {
      setActionLoading(true);
      setRelistError('');
      setError('');
      const response = await api.post(`/partner-packages/packages/${relistPkg.id}/relist`, dates, {
        headers: getAuthHeaders(),
      });
      const updated = response.data?.package;
      if (updated) {
        setPackages((current) => current.map((item) => (
          item.id === updated.id ? { ...item, ...updated } : item
        )));
      }
      setNotice(t('partnerLists.relistSuccess', { title: relistPkg.title }));
      setRelistPkg(null);
      setFilter('all');
      await fetchPackages();
    } catch (err) {
      setRelistError(err.response?.data?.error || t('partnerLists.relistFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async (pkg) => {
    try {
      setActionLoading(true);
      await api.post(`/partner-packages/packages/${pkg.id}/publish`, {}, {
        headers: getAuthHeaders()
      });
      fetchPackages();
    } catch (err) {
      setError(err.response?.data?.error === 'seller_reference_required'
        ? `${t('sellerDisclosures.missingTitle')}: ${t('sellerDisclosures.missingBody')}`
        : err.response?.data?.error || t('partnerLists.publishFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async (pkg) => {
    try {
      setActionLoading(true);
      await api.post(`/partner-packages/packages/${pkg.id}/unpublish`, {}, {
        headers: getAuthHeaders()
      });
      fetchPackages();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.unpublishFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleVisibility = async (pkg) => {
    const visibility = pkg.visibility === 'private_client' ? 'marketplace' : 'private_client';
    try {
      setActionLoading(true);
      await api.patch(`/partner-packages/packages/${pkg.id}/visibility`, { visibility }, {
        headers: getAuthHeaders()
      });
      setNotice(visibility === 'marketplace' ? t('partnerLists.madePublic') : t('partnerLists.madePrivate'));
      fetchPackages();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.visibilityFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPackage) return;

    try {
      setActionLoading(true);
      await api.delete(`/partner-packages/packages/${selectedPackage.id}`, {
        headers: getAuthHeaders()
      });
      setDeleteDialogOpen(false);
      fetchPackages();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.deleteFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = (pkg) => {
    navigate(`/partner/packages/create?duplicate=${pkg.id}`);
  };

  const handleClientCheckoutLink = (pkg) => {
    if (!pkg?.id) return;
    setError('');
    setNotice('');
    setBookForClientPkg(pkg);
  };

  const openPreview = (pkg) => {
    if (!pkg?.slug) {
      setError(t('partnerLists.previewFailed'));
      return;
    }
    setPreviewData({ id: pkg.id, slug: pkg.slug, title: pkg.title });
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const statusCopy = (pkg) => {
    const bucket = getPackageBucket(pkg);
    if (bucket === 'draft') return t('partnerLists.draftHidden');
    if (bucket === 'expired') {
      if (pkg.available_until) {
        return t('partnerLists.expiredOn', {
          date: format(new Date(pkg.available_until), 'd MMM yyyy'),
        });
      }
      return t('partnerLists.archivedHidden');
    }
    if (pkg.available_until) {
      return t('partnerLists.liveUntil', {
        date: format(new Date(pkg.available_until), 'd MMM yyyy'),
      });
    }
    return t('partnerLists.liveOngoing');
  };

  const counts = useMemo(() => ({
    all: packages.length,
    live: packages.filter((pkg) => getPackageListFilter(pkg) === 'live').length,
    private: packages.filter((pkg) => getPackageListFilter(pkg) === 'private').length,
    draft: packages.filter((pkg) => getPackageListFilter(pkg) === 'draft').length,
    expired: packages.filter((pkg) => getPackageListFilter(pkg) === 'expired').length,
  }), [packages]);

  const visiblePackages = useMemo(() => {
    const filtered = packages.filter((pkg) => {
      if (!packageMatchesListFilter(pkg, filter)) return false;
      return matchesQuery([pkg.title, pkg.destination, pkg.trip_code], searchQuery);
    });

    return [...filtered].sort((a, b) => {
      if (sort === 'price') return Number(b.base_price || 0) - Number(a.base_price || 0);
      if (sort === 'edited') {
        return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
      }
      return Number(b.booking_count || 0) - Number(a.booking_count || 0);
    });
  }, [packages, filter, searchQuery, sort]);

  const openEditor = (pkg) => navigate(`/partner/packages/${pkg.id}/edit`);

  return (
    <PartnerPageShell
      error={error}
      onClearError={() => setError('')}
      success={notice}
      onClearSuccess={() => setNotice('')}
    >
      <PartnerPageHeader
        title={t('partnerLists.packagesTitle')}
        subtitle={t('partnerLists.packagesSubDesktop')}
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              data-testid="copy-catalog-link"
              sx={partnerOxbloodBtnSx}
              onClick={handleCopyCatalogLink}
            >
              {catalogCopied ? t('partnerCatalog.copied') : t('partnerCatalog.copyLink')}
            </Button>
            <Button
              data-testid="copy-catalog-widget"
              sx={partnerOxbloodBtnSx}
              onClick={handleCopyWidgetSnippet}
            >
              {widgetCopied ? t('partnerCatalog.widgetCopied') : t('partnerCatalog.copyWidget')}
            </Button>
            <Box
              component="button"
              type="button"
              data-testid="rotate-catalog-link"
              onClick={handleRotateCatalogLink}
              disabled={catalogRotating}
              sx={partnerLinkSx}
            >
              {catalogRotating ? t('partnerCatalog.rotating') : t('partnerCatalog.rotate')}
            </Box>
            {!isMobile && (
              <Button
                sx={partnerOxbloodBtnSx}
                disabled={!sellerComplete}
                onClick={() => navigate('/partner/packages/create')}
                data-testid="new-package-button"
              >
                {t('partnerLists.newPackage')}
              </Button>
            )}
          </Box>
        }
      />
      <PartnerPackagesSubnav />

      {!sellerComplete && (
        <Alert
          severity="warning"
          data-testid="seller-reference-banner"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/partner/profile')}>
              {t('sellerDisclosures.goToProfile')}
            </Button>
          )}
        >
          <strong>{t('sellerDisclosures.missingTitle')}:</strong> {t('sellerDisclosures.missingBody')}
        </Alert>
      )}

      {isMobile && (
        <PartnerNote>
          {customizeMode
            ? t('partner.opsHome.customizeHint')
            : t('partner.opsHome.createPackageDesktop')}
        </PartnerNote>
      )}

      <PartnerFilterBar
        chips={[
          { id: 'all', label: t('partnerLists.filterAll'), count: counts.all },
          { id: 'live', label: t('partnerLists.filterLive'), count: counts.live },
          { id: 'private', label: t('partnerLists.filterPrivate'), count: counts.private },
          { id: 'draft', label: t('partnerLists.filterDraft'), count: counts.draft },
          { id: 'expired', label: t('partnerLists.filterExpired'), count: counts.expired },
        ]}
        activeChip={filter}
        onChipChange={setFilter}
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('partnerLists.searchPackages')}
        sort={sort}
        onSortChange={setSort}
        sortOptions={[
          { id: 'booked', label: t('partnerLists.sortBooked') },
          { id: 'edited', label: t('partnerLists.sortEdited') },
          { id: 'price', label: t('partnerLists.sortPrice') },
        ]}
      />

      {loading ? (
        <PartnerLoading />
      ) : visiblePackages.length === 0 ? (
        <PartnerEmptyState
          title={searchQuery || filter !== 'all' ? t('partnerLists.noResults') : t('partnerLists.noPackages')}
          body={searchQuery || filter !== 'all' ? t('partnerLists.noResultsBody') : t('partnerLists.noPackagesBody')}
          action={!searchQuery && filter === 'all' && !isMobile ? (
            <Button sx={partnerOxbloodBtnSx} disabled={!sellerComplete} onClick={() => navigate('/partner/packages/create')}>
              {t('partnerLists.newPackage')}
            </Button>
          ) : null}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mt: 0.5,
            alignItems: 'stretch',
          }}
        >
          {visiblePackages.map((pkg) => {
            const bucket = getPackageBucket(pkg);
            const dest = getDestinationLabel(pkg.destination);
            const nights = Math.max(0, Number(pkg.duration_days || 1) - 1);
            const hiddenStats = bucket === 'draft';
            const statusLabel = bucket === 'live'
              ? t('partnerLists.filterLive')
              : bucket === 'expired'
                ? t('partnerLists.filterExpired')
                : t('partnerLists.filterDraft');
            const visibilityItem = {
              label: pkg.visibility === 'private_client' ? t('partnerLists.makePublic') : t('partnerLists.makePrivate'),
              onClick: () => handleToggleVisibility(pkg),
            };
            const menuItems = bucket === 'draft'
              ? [
                { label: t('partnerLists.publish'), onClick: () => handlePublish(pkg) },
                visibilityItem,
                {
                  label: t('partnerLists.delete'),
                  danger: true,
                  onClick: () => {
                    setSelectedPackage(pkg);
                    setDeleteDialogOpen(true);
                  },
                },
              ]
              : bucket === 'live'
                ? [
                  {
                    label: t('partner.opsHome.customizeForClient'),
                    onClick: () => navigate(`/trips/create?fromPackage=${pkg.id}`),
                  },
                  {
                    label: t('partnerLists.clientLink'),
                    onClick: () => handleClientCheckoutLink(pkg),
                  },
                  visibilityItem,
                  { label: t('partnerLists.duplicate'), onClick: () => handleDuplicate(pkg) },
                  { label: t('partnerLists.unpublish'), onClick: () => handleUnpublish(pkg) },
                ]
                : [
                  {
                    label: t('partnerLists.relist'),
                    onClick: () => {
                      setRelistError('');
                      setRelistPkg(pkg);
                    },
                  },
                  {
                    label: t('partner.opsHome.customizeForClient'),
                    onClick: () => navigate(`/trips/create?fromPackage=${pkg.id}`),
                  },
                  visibilityItem,
                  { label: t('partnerLists.duplicate'), onClick: () => handleDuplicate(pkg) },
                  { label: t('partnerLists.archive'), onClick: () => handleUnpublish(pkg) },
                ];

            const primaryLabel = customizeMode && bucket !== 'draft'
              ? t('partner.opsHome.customizeForClient')
              : bucket === 'draft'
                ? t('partnerLists.continue')
                : bucket === 'expired'
                  ? t('partnerLists.relist')
                  : t('partnerLists.edit');
            const onPrimary = () => {
              if (customizeMode && bucket !== 'draft') {
                navigate(`/trips/create?fromPackage=${pkg.id}`);
                return;
              }
              if (bucket === 'expired') {
                setRelistError('');
                setRelistPkg(pkg);
                return;
              }
              openEditor(pkg);
            };

            return (
              <PartnerWorkspaceCard
                key={pkg.id}
                testId="partner-package-card"
                layout="wide"
                coverSrc={getPackageCoverSrc(pkg)}
                fallbackSrc={getDestinationPhotoUrl(pkg.destination, pkg.id)}
                alt={pkg.title}
                chips={[
                  { label: statusLabel, tone: bucket === 'live' ? 'accent' : 'status' },
                  pkg.visibility === 'private_client'
                    ? { label: t('partnerLists.privateClient'), tone: 'accent' }
                    : null,
                  isPackageInPublicCatalog(pkg, entitlements)
                    ? { label: t('partnerLists.inCatalog'), tone: 'catalog' }
                    : null,
                  isPackageHighlighted(pkg, entitlements)
                    ? { label: t('partnerLists.highlighted'), tone: 'highlight' }
                    : null,
                  pkg.package_type ? { label: pkg.package_type, tone: 'type' } : null,
                ].filter(Boolean)}
                icon={<PackageIcon />}
                onClick={() => openPreview(pkg)}
                menuItems={menuItems}
                actionLabel={primaryLabel}
                actionIcon={<EditIcon />}
                onAction={onPrimary}
              >
                <Typography variant="h6" component="h3" sx={partnerCardTitleWideSx}>
                  {pkg.title}
                </Typography>
                <Typography variant="caption" sx={{ color: DOCUMENT.ink3, display: 'block', mb: 1 }}>
                  {statusCopy(pkg)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minHeight: 0 }}>
                  <LocationIcon sx={{ fontSize: 16, color: DOCUMENT.ink3, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: DOCUMENT.ink2 }} noWrap>
                    {pkg.destination || dest || t('partnerLists.noDestination')}
                    {' · '}
                    {pkg.duration_days || 0} {t('partnerLists.daysWord')}
                  </Typography>
                </Box>

                <PartnerCardDatePanel
                  inline
                  startLabel={t('partnerLists.sellsFrom')}
                  endLabel={t('partnerLists.sellsUntil')}
                  startValue={pkg.available_from ? format(new Date(pkg.available_from), 'MMM d') : t('partnerLists.dash')}
                  endValue={pkg.available_until ? format(new Date(pkg.available_until), 'MMM d') : t('partnerLists.dash')}
                  caption={`${pkg.available_from ? format(new Date(pkg.available_from), 'yyyy') : ''} · ${nights} ${t('partnerLists.nightsWord')}`}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, minHeight: 0 }}>
                  <PriceIcon sx={{ fontSize: 16, color: DOCUMENT.ink3 }} />
                  <Typography variant="body2" sx={{ color: DOCUMENT.ink2, fontWeight: 700 }} noWrap>
                    {formatMoney(pkg.base_price)}
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 500, color: DOCUMENT.ink3 }}>
                      {t('partnerLists.perPerson')}
                    </Typography>
                  </Typography>
                  {!hiddenStats ? (
                    <Typography
                      component="button"
                      variant="caption"
                      sx={{
                        ml: 'auto',
                        background: 'none',
                        border: 0,
                        p: 0,
                        font: 'inherit',
                        fontSize: 12,
                        fontWeight: 600,
                        color: DOCUMENT.ink3,
                        cursor: 'pointer',
                        '&:hover': { color: DOCUMENT.oxblood },
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/partner/bookings?package=${pkg.id}`);
                      }}
                    >
                      {t('partnerLists.booked')}: {pkg.booking_count || 0}
                    </Typography>
                  ) : null}
                </Box>
              </PartnerWorkspaceCard>
            );
          })}
        </Box>
      )}

      {introductionFeeRate != null && (
        <PartnerNote>
          {t('partnerLists.packagesNote', {
            plan: t(planNameKey(entitlements?.tier)),
            rate: introductionFeeRate,
            ...introductionFeeCopyVars(),
          })}
        </PartnerNote>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('partnerLists.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('partnerLists.deleteBody', { title: selectedPackage?.title })}
          </Typography>
          {selectedPackage?.booking_count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('partnerLists.deleteWarning', { count: selectedPackage.booking_count })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('partnerLists.cancel')}</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : t('partnerLists.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPageOverlay
        open={previewOpen}
        onClose={closePreview}
        title={t('partnerLists.preview')}
        testId="package-preview-modal"
        actions={(
          <>
            {previewData?.id ? (
              <Button
                size="small"
                onClick={() => {
                  closePreview();
                  navigate(`/partner/packages/${previewData.id}/edit`);
                }}
                sx={documentOverlayActionSx}
              >
                {t('partnerLists.edit')}
              </Button>
            ) : null}
            <Button
              size="small"
              onClick={() => window.open(`/packages/${previewData?.slug}?preview=1`, '_blank')}
              disabled={!previewData?.slug}
              sx={documentOverlayActionSx}
            >
              {t('partnerLists.openTab')}
            </Button>
          </>
        )}
      >
        {previewData?.slug ? (
          <PublicPackageDetails previewSlug={previewData.slug} isPreviewMode />
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">{t('partnerLists.previewFailed')}</Typography>
          </Box>
        )}
      </DocumentPageOverlay>
      <RelistPackageDialog
        pkg={relistPkg}
        open={Boolean(relistPkg)}
        loading={actionLoading}
        error={relistError}
        onClose={() => {
          if (actionLoading) return;
          setRelistPkg(null);
          setRelistError('');
        }}
        onSubmit={handleRelist}
        onOpenEditor={() => {
          if (!relistPkg?.id) return;
          const id = relistPkg.id;
          setRelistPkg(null);
          navigate(`/partner/packages/${id}/edit`);
        }}
      />
      <BookForClientDialog
        open={Boolean(bookForClientPkg)}
        onClose={() => setBookForClientPkg(null)}
        packageId={bookForClientPkg?.id}
        packageTitle={bookForClientPkg?.title}
        currency={bookForClientPkg?.currency || 'USD'}
        pricePerPerson={bookForClientPkg?.base_price || 0}
        onSent={(result) => {
          setNotice(t('bookForClient.sent', {
            email: result.email,
            ref: result.bookingReference,
          }));
        }}
      />
    </PartnerPageShell>
  );
};

export default PartnerPackages;
